/** @jsxImportSource @emotion/react */
import { presentUserOrgRole } from '@kaiizen/kai-presenters';
import {
  API_KEY_DEFAULT_EXPIRATION_DAYS,
  API_KEY_MAXIMUM_EXPIRATION_DAYS,
  API_KEY_MAXIMUM_EXPIRATION_MS,
  ApiApiKeyRoles,
} from '@kaiizen/kai-pro-api';
import type { ApiApiKeyRole } from '@kaiizen/kai-pro-api';
import { useApiKeys } from '@kaiizen/kai-pro-api-react-state';
import { CommonField } from '@kaiizen/kai-shiki/react';
import { useEffect, useState } from 'react';

import { useToast } from '@/Client/State';
import { LabeledSelect } from '@/Client/Widgets';
import {
  MODAL_BASE_PRIMARY_BUTTON_STYLE,
  MODAL_BASE_SECONDARY_BUTTON_STYLE,
  ModalBase,
} from '@/Client/Widgets/ModalBase';
import { DEFAULT_CREATE_API_KEY_MODAL_STYLES } from './createApiKeyModalStyles';
import type { CreateApiKeyModalStyles } from './createApiKeyModalStyles';
import {
  ORG_SETTINGS_BODY_TEXT_STYLE,
  ORG_SETTINGS_SUBHEADING_STYLE,
} from './OrgSettingsStyleLib';

const ROLE_OPTIONS = ApiApiKeyRoles.map(role => ({ value: role, label: presentUserOrgRole(role) }));

const getDateInputValue = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const getDateInputValueAfterDays = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return getDateInputValue(date);
};
const getExpirationAt = (expiresOn: string) => {
  const [year, month, day] = expiresOn.split('-').map(Number);
  const localEndOfDay = new Date(year!, month! - 1, day!, 23, 59, 59, 999);
  return new Date(Math.min(localEndOfDay.getTime(), Date.now() + API_KEY_MAXIMUM_EXPIRATION_MS));
};
const getDefaultExpirationValue = () => getDateInputValueAfterDays(API_KEY_DEFAULT_EXPIRATION_DAYS);

/** Collects a new or replacement key's details, then reveals its secret exactly once. */
export const CreateApiKeyModal = ({
  onClose,
  machineUserId,
  styles = DEFAULT_CREATE_API_KEY_MODAL_STYLES,
}: {
  onClose: () => void;
  machineUserId?: string;
  styles?: CreateApiKeyModalStyles;
}) => {
  const { apiKeyMutators } = useApiKeys();
  const { tryWithErrorToast } = useToast();
  const [name, setName] = useState('');
  const [role, setRole] = useState<ApiApiKeyRole>('fieldUser');
  const [expiresOn, setExpiresOn] = useState(getDefaultExpirationValue);
  const [isCreating, setIsCreating] = useState(false);
  const [rawKey, setRawKey] = useState<string>();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !rawKey) onClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose, rawKey]);

  const createApiKey = async () => {
    if ((!machineUserId && !name.trim()) || !expiresOn || isCreating) return;
    setIsCreating(true);
    try {
      const expiresAt = getExpirationAt(expiresOn);
      const created = await tryWithErrorToast('create the API key', () =>
        apiKeyMutators.create(
          machineUserId
            ? { machineUserId, expiresAt }
            : { name: name.trim(), role, expiresAt },
        ));
      if (created) setRawKey(created.rawKey);
    } finally {
      setIsCreating(false);
    }
  };

  const copyRawKey = async () => {
    if (!rawKey) return;
    await navigator.clipboard.writeText(rawKey);
    setIsCopied(true);
  };

  return (
    <ModalBase
      actions={rawKey
        ? <button css={MODAL_BASE_PRIMARY_BUTTON_STYLE} onClick={onClose} type='button'>Done</button>
        : (
          <>
            <button css={MODAL_BASE_SECONDARY_BUTTON_STYLE} onClick={onClose} type='button'>Cancel</button>
            <button
              css={MODAL_BASE_PRIMARY_BUTTON_STYLE}
              disabled={(!machineUserId && !name.trim()) || !expiresOn || isCreating}
              onClick={createApiKey}
              type='button'
            >
              {isCreating ? 'Creating…' : machineUserId ? 'Create replacement key' : 'Create key'}
            </button>
          </>
        )}
      maxWidth={640}
      onOverlayClick={rawKey ? undefined : onClose}
      title={rawKey ? 'Copy API key' : machineUserId ? 'Create replacement key' : 'Create API key'}
    >
      {rawKey
        ? (
          <>
            <div css={ORG_SETTINGS_SUBHEADING_STYLE}>Copy this key now</div>
            <div css={ORG_SETTINGS_BODY_TEXT_STYLE}>It is shown only once and cannot be retrieved after you close this dialog.</div>
            <pre css={styles.secret}>{rawKey}</pre>
            <div css={styles.copyActions}>
              <button css={MODAL_BASE_SECONDARY_BUTTON_STYLE} onClick={copyRawKey} type='button'>{isCopied ? 'Copied' : 'Copy key'}</button>
              {isCopied && <div css={ORG_SETTINGS_BODY_TEXT_STYLE}>Store it in your integration’s secret manager.</div>}
            </div>
          </>
        )
        : (
          <div css={styles.form}>
            {machineUserId
              ? (
                <div css={[styles.fullWidthField, ORG_SETTINGS_BODY_TEXT_STYLE]}>
                  A replacement key uses this machine identity’s existing name and access role.
                </div>
              )
              : (
                <>
                  <div css={styles.fullWidthField}>
                    <CommonField label='Machine identity name' inputProps={{ autoFocus: true, value: name, onChange: event => setName(event.target.value) }} />
                  </div>
                  <LabeledSelect label='Access role' value={role} items={ROLE_OPTIONS} onUpdate={value => setRole(value as ApiApiKeyRole)} />
                </>
              )}
            <CommonField
              label='Expiration'
              inputProps={{
                autoFocus: Boolean(machineUserId),
                'aria-label': 'Expiration',
                min: getDateInputValueAfterDays(1),
                max: getDateInputValueAfterDays(API_KEY_MAXIMUM_EXPIRATION_DAYS),
                type: 'date',
                value: expiresOn,
                onChange: event => setExpiresOn(event.target.value),
              }}
            />
          </div>
        )}
    </ModalBase>
  );
};
